import { Outlet, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?inline";
import oswaldWoff2 from "../fonts/oswald-latin-wght.woff2?url";
import barlow400Woff2 from "../fonts/barlow-latin-400.woff2?url";
import barlow600Woff2 from "../fonts/barlow-latin-600.woff2?url";
import { DEFERRED_ANALYTICS_INLINE } from "../components/site/deferred-analytics";
import { HOST_SCRIPT_IDLE_LOADER, HOST_SCRIPT_STEAL_INLINE } from "../lib/host-script-loader";

const BARLOW_UNICODE_RANGE =
  "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD";

/** Injected after first paint so Barlow is not a critical-path CSS discovery. */
const deferredBarlowFontFaceCss = `
@font-face{font-family:Barlow;font-style:normal;font-display:swap;font-weight:400;src:url(${JSON.stringify(barlow400Woff2)}) format("woff2");unicode-range:${BARLOW_UNICODE_RANGE}}
@font-face{font-family:Barlow;font-style:normal;font-display:swap;font-weight:600;src:url(${JSON.stringify(barlow600Woff2)}) format("woff2");unicode-range:${BARLOW_UNICODE_RANGE}}
`.trim();

const Toaster = lazy(() =>
  import("../components/ui/sonner").then((mod) => ({ default: mod.Toaster })),
);

const NotFoundComponent = lazy(() => import("../components/site/not-found"));
const ErrorComponent = lazy(() => import("../components/site/route-error"));

function LazyRouteFallback() {
  return (
    <div
      id="main-content"
      tabIndex={-1}
      className="flex min-h-screen items-center justify-center bg-background px-4 text-sm text-muted-foreground"
    >
      Loading…
    </div>
  );
}

function LazyNotFound() {
  return (
    <Suspense fallback={<LazyRouteFallback />}>
      <NotFoundComponent />
    </Suspense>
  );
}

function LazyError(props: { error: Error; reset: () => void }) {
  return (
    <Suspense fallback={<LazyRouteFallback />}>
      <ErrorComponent {...props} />
    </Suspense>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, viewport-fit=cover",
      },
      { title: "Livengood Pallet Service" },
      {
        name: "description",
        content:
          "Used, new, and combo pallets plus pallet removal and recycling in Frankfort, Indiana.",
      },
      { name: "author", content: "Livengood Pallet Service" },
      {
        name: "google-site-verification",
        content: "mfLuCnGMkukCGZSep63urtmDahGOkRFEujpIGDMAw34",
      },
      { name: "theme-color", content: "#1c1917" },
      { name: "color-scheme", content: "light" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Livengood Pallet Service" },
      {
        property: "og:description",
        content:
          "Used, new, and combo pallets plus pallet removal and recycling in Frankfort, Indiana.",
      },
      { property: "og:url", content: "https://livengoodpalletservice.com/" },
      {
        property: "og:image",
        content: "https://livengoodpalletservice.com/og-image.png",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Livengood Pallet Service" },
      {
        name: "twitter:description",
        content:
          "Used, new, and combo pallets plus pallet removal and recycling in Frankfort, Indiana.",
      },
      {
        name: "twitter:image",
        content: "https://livengoodpalletservice.com/og-image.png",
      },
    ],
    links: [
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "canonical", href: "https://livengoodpalletservice.com/" },
      // dns-prefetch only — do not preconnect; gtag loads on interaction only.
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: LazyNotFound,
  errorComponent: LazyError,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    // motion-ready is added by a post-paint inline script before hydration;
    // suppress so that class does not trigger a hydration mismatch reflow.
    <html lang="en" suppressHydrationWarning>
      <head>
        {/* Steal flock/events defer tags before they become early long tasks. */}
        <script dangerouslySetInnerHTML={{ __html: HOST_SCRIPT_STEAL_INLINE }} />
        <style dangerouslySetInnerHTML={{ __html: appCss }} />
        <link
          rel="preload"
          href={oswaldWoff2}
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
          fetchPriority="high"
        />
        <HeadContent />
      </head>
      <body>
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        {children}
        <script
          dangerouslySetInnerHTML={{
            // Idle-gate intro motion so board-snap / word-rise are not one long style task during hydration.
            __html:
              "(function(){function go(){if(!matchMedia('(prefers-reduced-motion: reduce)').matches)document.documentElement.classList.add('motion-ready')}if('requestIdleCallback' in window)requestIdleCallback(go,{timeout:1800});else setTimeout(go,1)})()",
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `requestAnimationFrame(function(){var s=document.createElement('style');s.setAttribute('data-fonts','barlow');s.textContent=${JSON.stringify(deferredBarlowFontFaceCss)};document.head.appendChild(s)})`,
          }}
        />
        {/* Interaction-only gtag — not downloaded during Lighthouse unused-JS coverage. */}
        <script dangerouslySetInnerHTML={{ __html: DEFERRED_ANALYTICS_INLINE }} />
        <script dangerouslySetInnerHTML={{ __html: HOST_SCRIPT_IDLE_LOADER }} />
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <DeferredToaster />
    </>
  );
}

function DeferredToaster() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const enable = () => setShow(true);
    window.addEventListener("pointerdown", enable, { once: true, passive: true });
    window.addEventListener("keydown", enable, { once: true });
    window.addEventListener("app:need-toaster", enable);
    return () => {
      window.removeEventListener("pointerdown", enable);
      window.removeEventListener("keydown", enable);
      window.removeEventListener("app:need-toaster", enable);
    };
  }, []);
  if (!show) return null;
  return (
    <Suspense fallback={null}>
      <Toaster />
    </Suspense>
  );
}
